/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Text from "../ui/Text";
import TextCircleDivider from "../ui/TextCircleDivider";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/Tooltip";

const StyledLineItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;

  ${({ $applyMobileStyles, theme }) => $applyMobileStyles && theme.containerMedia.sm} {
    flex-direction: column;
    align-items: flex-start;
    max-width: 100%;
    gap: 0;
  }
`;

const StyledTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;

  ${({ $applyMobileStyles, theme }) => $applyMobileStyles && theme.containerMedia.sm} {
    max-width: 100%;
  }
`;

const StyledTooltipTrigger = styled((props) => <TooltipTrigger {...props} />)`
  min-width: 0;
`;

const StyledName = styled((props) => <Text {...props} />)`
  ${(props) => !props.$showTooltip && "min-width: 0;"}
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledMetadata = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${({ $applyMobileStyles, theme }) => $applyMobileStyles && theme.containerMedia.sm} {
    > *:first-child {
      display: none;
    }
  }
`;

const StyledQuantity = styled(Text)`
  flex-shrink: 0;
`;

const LineItem = ({ quantity, name, metadata = [], showTooltip = false, applyMobileStyles = false, size = "default", children, ...props }) => {
  if (children) {
    return <StyledLineItem $applyMobileStyles={applyMobileStyles} {...props}>{children}</StyledLineItem>;
  }

  return (
    <StyledLineItem $applyMobileStyles={applyMobileStyles} {...props}>
      <StyledTitle $applyMobileStyles={applyMobileStyles}>
        {quantity && <StyledQuantity size={size}>{quantity}x</StyledQuantity>}

        {showTooltip && (
          <Tooltip placement="top-left">
            <StyledTooltipTrigger>
              <StyledName color="dark" size={size} $showTooltip={showTooltip}>
                {name}
              </StyledName>
            </StyledTooltipTrigger>
            <TooltipContent>
              <Text size="small">{name}</Text>
            </TooltipContent>
          </Tooltip>
        )}

        {!showTooltip && <StyledName color="dark" size={size}>{name}</StyledName>}
      </StyledTitle>

      {metadata && Boolean(metadata.length > 0) && (
        <StyledMetadata $applyMobileStyles={applyMobileStyles}>
          {metadata.filter(Boolean).map((item, index) => {
            return (
              <React.Fragment key={index}>
                <TextCircleDivider />
                {item}
              </React.Fragment>
            );
          })}
        </StyledMetadata>
      )}
    </StyledLineItem>
  );
};

export default LineItem;
